import { createStore, useStore } from './store';

/**
 * Per item+field save state. A field in the `saving` state is locked: further edits of the same
 * field are ignored until the request settles (duplicate-submission guard).
 */
export interface FieldState {
  status: 'saving' | 'saved' | 'error';
  message?: string;
}

const store = createStore<ReadonlyMap<string, FieldState>>(new Map());
const timers = new Map<string, ReturnType<typeof setTimeout>>();

export const fieldKey = (kind: string, id: string, field: string): string => `${kind}:${id}:${field}`;

export function getFieldState(key: string): FieldState | undefined {
  return store.get().get(key);
}

export function isLocked(key: string): boolean {
  return getFieldState(key)?.status === 'saving';
}

export function setFieldState(key: string, state: FieldState | null, clearAfterMs?: number): void {
  const next = new Map(store.get());
  if (state) next.set(key, state);
  else next.delete(key);
  store.set(next);
  const t = timers.get(key);
  if (t) clearTimeout(t);
  timers.delete(key);
  if (state && clearAfterMs) {
    timers.set(
      key,
      setTimeout(() => {
        timers.delete(key);
        const cur = store.get().get(key);
        if (cur === state) setFieldState(key, null);
      }, clearAfterMs),
    );
  }
}

export function markSaving(keys: string[]): void {
  keys.forEach((k) => setFieldState(k, { status: 'saving' }));
}
export function markSaved(keys: string[]): void {
  keys.forEach((k) => setFieldState(k, { status: 'saved' }, 1400));
}
export function markError(keys: string[], message: string): void {
  keys.forEach((k) => setFieldState(k, { status: 'error', message }, 8000));
}

export function useFieldState(key: string): FieldState | undefined {
  return useStore(store).get(key);
}

/** True while any field of the item is saving (used to dim rows of freshly created temp items). */
export function useItemSaving(kind: string, id: string): boolean {
  const map = useStore(store);
  const prefix = `${kind}:${id}:`;
  for (const [k, v] of map) if (v.status === 'saving' && k.startsWith(prefix)) return true;
  return false;
}
